import { formatPrice } from '@/lib/formatPrice';
import type { Locale } from '@/lib/i18n';
import { isSupportedCurrency } from './currency-metadata';

export interface DisplayPriceInput {
  amount: string | number;
  from: string;
  to: string;
}

export interface DisplayPriceResult {
  text: string;
  converted: string | null;
  rate: number | null;
}

function toFiniteNumber(amount: string | number): number | null {
  const n = typeof amount === 'string' ? Number(amount) : amount;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

function rateFor(rates: Record<string, number> | null, code: string): number | null {
  if (!rates) return null;
  const v = rates[code.toLowerCase()];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function formatConverted(value: number, code: string, locale: Locale): string {
  const upper = code.toUpperCase();
  try {
    // Throws RangeError for non-ISO codes (e.g. USDT, BTC).
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: upper,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    const decimal = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `${decimal} ${upper}`;
  }
}

/**
 * Format a price for display in a target currency.
 *
 * `rates` are per-1-USDT tables (see currency-rates). Amounts are
 * decimal-string-safe: non-finite input short-circuits to the canonical
 * fallback and no float math leaks anywhere except the converted string.
 * Unknown `to` codes convert to null. No user strings here — consumers
 * prepend their own localized approximate prefix to `converted`.
 */
export function formatDisplayPrice(
  input: DisplayPriceInput,
  rates: Record<string, number> | null,
  locale: Locale,
): DisplayPriceResult {
  const to = input.to.toUpperCase();
  const fallback: DisplayPriceResult = {
    text: formatPrice(input.amount, input.from, locale),
    converted: null,
    rate: null,
  };
  if (!isSupportedCurrency(to)) return fallback;
  const amount = toFiniteNumber(input.amount);
  if (amount === null) return fallback;
  const rate = rateFor(rates, to);
  if (rate === null) return fallback;
  const from = input.from.toUpperCase();
  let inUsdt: number | null;
  if (from === 'USDT') {
    inUsdt = amount;
  } else {
    const fromRate = rateFor(rates, from);
    inUsdt = fromRate !== null && fromRate !== 0 ? amount / fromRate : null;
  }
  if (inUsdt === null || !Number.isFinite(inUsdt)) return { ...fallback, rate };
  const converted = formatConverted(inUsdt * rate, to, locale);
  return { text: converted, converted, rate };
}
